import React, { useState, useEffect } from 'react';

// Phased wait copy for the async generation. The real wait is model-dominated
// (p50 ~15s, tail to ~90s), so a single static "10-20 sekunder" line felt
// wrong on the long tail. Each phase keeps the parent calm without claiming a
// fixed time; the message advances by elapsed seconds.
const GENERATION_PHASES = [
  { until: 8, message: 'Henter fram opplysningene du har gitt meg …' },
  { until: 18, message: 'Går nøye gjennom alle svarene dine …' },
  { until: 25, message: 'Nå har jeg det jeg trenger …' },
  { until: 52, message: 'Skriver søknaden – skreddersydd for situasjonen deres …' },
  { until: 72, message: 'Leser gjennom og finpusser teksten …' },
  { until: 120, message: 'Legger på de siste detaljene – jeg vil ha den helt riktig for deg …' },
];
const GENERATION_SLOW_MESSAGE =
  'Dette tar litt lengre tid enn vanlig i dag, men søknaden er på vei. Takk for tålmodigheten.';

function generationMessage(elapsedSeconds) {
  const phase = GENERATION_PHASES.find(p => elapsedSeconds < p.until);
  return phase ? phase.message : GENERATION_SLOW_MESSAGE;
}

/**
 * SummaryStep - Displays a structured review of all wizard selections
 * before generating the søknad.
 *
 * Features:
 * - Collapsible sections for each stage
 * - Shows checked items, helper info, free text
 * - "Rediger" links to navigate back to specific steps
 * - "Lag søknaden" button to trigger AI generation
 * - Loading state with empathetic, time-aware message
 */
export function SummaryStep({
  config,
  stageData,
  onEditStep,
  onGenerate,
  isGenerating,
  generateError,
}) {
  const [expandedSections, setExpandedSections] = useState(() => {
    // Start with all sections expanded
    const expanded = {};
    config.steps.forEach(step => {
      if (!step.isSummaryStep) {
        expanded[step.id] = true;
      }
    });
    return expanded;
  });

  // Seconds elapsed since generation started — drives the phased wait copy.
  const [genElapsed, setGenElapsed] = useState(0);
  useEffect(() => {
    if (!isGenerating) {
      setGenElapsed(0);
      return undefined;
    }
    setGenElapsed(0);
    const startedAt = Date.now();
    const id = setInterval(() => {
      setGenElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [isGenerating]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Helper to get checkbox label from config
  const getCheckboxLabel = (step, itemId) => {
    // Check in regular checkboxItems
    const item = step.checkboxItems?.find(i => i.id === itemId);
    if (item) return item.label;

    // Check in sections (for support step)
    if (step.sections) {
      for (const section of step.sections) {
        const sectionItem = section.checkboxItems?.find(i => i.id === itemId);
        if (sectionItem) return sectionItem.label;
      }
    }

    return itemId;
  };

  // Helper to get helper name label
  const getHelperLabel = (helperValue) => {
    if (!helperValue) return null;
    const option = config.helperOptions?.find(o => o.value === helperValue);
    return option ? option.label : helperValue;
  };

  // Check if a stage has any selections
  const hasSelections = (stepId) => {
    const data = stageData[stepId];
    if (!data) return false;

    // Check if night step was skipped via gate question
    if (stepId === 'night' && data.gateAnswer === true) {
      return true; // Show "Ikke aktuelt"
    }

    return (
      data.checkedItems?.length > 0 ||
      data.freeText?.trim().length > 0 ||
      data.primaryHelper ||
      data.estimatedHours
    );
  };

  // Get display steps. Exclude the summary step itself, plus the structural
  // intro/confirm-subject steps — those are not "answers" the user reviews here,
  // they set WHO the søknad is about. (See isIntroStep / isSubjectConfirmStep in
  // the stønad config.)
  const displaySteps = config.steps.filter(
    step => !step.isSummaryStep && !step.isIntroStep && !step.isSubjectConfirmStep
  );

  // LANDMINE FIX (PR: person-picker). The edit-jump must navigate to a position
  // in the UNFILTERED config.steps array, because wizard.goToStep indexes
  // config.steps directly. Previously this passed the FILTERED displaySteps
  // index, which only worked by coincidence (summary was the last element, so
  // filtering it shifted no earlier index). Prepending an intro step at index 0
  // and inserting a conditional confirm step breaks that coincidence and would
  // make every "Rediger" jump land on the wrong step. Resolving the real index
  // by step.id is immune to any prepend / insert / conditional-presence change —
  // it keys off identity, not position, so no fragile numeric offset is needed.
  const realStepIndex = (stepId) =>
    config.steps.findIndex(s => s.id === stepId);

  return (
    <div>
      <h2 className="text-2xl font-semibold text-text-primary mb-2">
        Sammendrag
      </h2>
      <p className="text-text-secondary mb-6">
        Se over valgene dine før du lager søknaden. Du kan gå tilbake og endre hvis noe mangler.
      </p>

      {/* Stage sections */}
      <div className="space-y-4 mb-8">
        {displaySteps.map((step) => {
          const data = stageData[step.id] || {};
          const isExpanded = expandedSections[step.id];
          const hasContent = hasSelections(step.id);
          const isNightSkipped = step.id === 'night' && data.gateAnswer === true;

          return (
            <div
              key={step.id}
              className="border border-border-light rounded-xl overflow-hidden"
            >
              {/* Section header */}
              <button
                type="button"
                onClick={() => toggleSection(step.id)}
                className="w-full flex items-center justify-between p-4 bg-background-card hover:bg-neutral-100 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg font-medium text-text-primary">
                    {step.title}
                  </span>
                  {!hasContent && !isNightSkipped && (
                    <span className="text-sm text-text-muted px-2 py-0.5 bg-neutral-100 rounded">
                      Ingen valg
                    </span>
                  )}
                  {isNightSkipped && (
                    <span className="text-sm text-sage-600 px-2 py-0.5 bg-sage-50 rounded">
                      Ikke aktuelt
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Pass the REAL config.steps index (resolved by id), not the
                      // filtered-list position — see realStepIndex note above.
                      onEditStep(realStepIndex(step.id));
                    }}
                    className="text-sm text-primary hover:text-primary-600 font-medium"
                  >
                    Rediger
                  </button>
                  <svg
                    className={`w-5 h-5 text-text-muted transition-transform ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              {/* Section content */}
              {isExpanded && (
                <div className="p-4 border-t border-border-light bg-white">
                  {isNightSkipped ? (
                    <p className="text-text-secondary italic">
                      Du og barnet ditt sover som regel hele natten.
                    </p>
                  ) : !hasContent ? (
                    <p className="text-text-muted italic">
                      Ingen avkrysninger eller tekst lagt til i denne delen.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {/* Checked items */}
                      {data.checkedItems?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-text-secondary mb-2">
                            Avkrysset:
                          </h4>
                          <ul className="space-y-1">
                            {data.checkedItems.map(itemId => (
                              <li
                                key={itemId}
                                className="flex items-start gap-2 text-text-primary"
                              >
                                <svg
                                  className="w-5 h-5 text-sage-500 flex-shrink-0 mt-0.5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                                <span>{getCheckboxLabel(step, itemId)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Helper info */}
                      {(data.primaryHelper || data.estimatedHours) && (
                        <div className="flex flex-wrap gap-4 text-sm">
                          {data.primaryHelper && (
                            <div className="flex items-center gap-2">
                              <span className="text-text-secondary">Hvem:</span>
                              <span className="font-medium text-text-primary">
                                {getHelperLabel(data.primaryHelper)}
                              </span>
                            </div>
                          )}
                          {data.estimatedHours && (
                            <div className="flex items-center gap-2">
                              <span className="text-text-secondary">Timer:</span>
                              <span className="font-medium text-text-primary">
                                {data.estimatedHours}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Free text */}
                      {data.freeText?.trim() && (
                        <div>
                          <h4 className="text-sm font-medium text-text-secondary mb-1">
                            Tillegg:
                          </h4>
                          <p className="text-text-primary bg-neutral-50 p-3 rounded-lg text-sm">
                            {data.freeText}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Error message */}
      {generateError && (
        <div className="mb-6 p-4 bg-error-50 border border-error-200 rounded-xl">
          <p className="text-error-700">{generateError}</p>
        </div>
      )}

      {/* Generate button */}
      <div className="text-center">
        {isGenerating ? (
          <div className="bg-sage-50 border border-sage-200 rounded-xl p-8">
            <div className="flex flex-col items-center">
              <div className="animate-spin w-10 h-10 border-4 border-sage-300 border-t-sage-600 rounded-full mb-4"></div>
              <p className="text-lg font-medium text-sage-800 mb-2">
                {generationMessage(genElapsed)}
              </p>
              <p className="text-sage-600 text-sm max-w-md">
                {genElapsed >= 120
                  ? 'Svarene dine er trygt lagret.'
                  : 'Søknaden blir skreddersydd for situasjonen deres. Det tar vanligvis under ett minutt.'}
              </p>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={onGenerate}
            className="
              inline-flex items-center gap-2
              px-8 py-4 rounded-xl
              bg-primary text-white font-semibold text-lg
              hover:bg-primary-600 hover:shadow-primary
              active:transform active:scale-95
              transition-all duration-200
            "
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {generateError ? 'Prøv igjen' : 'Lag søknaden'}
          </button>
        )}
      </div>

      {/* Additional info */}
      <div className="mt-6 text-center">
        <p className="text-sm text-text-muted">
          Søknaden genereres basert på det du har fortalt oss. Du kan alltid
          gå tilbake og endre etterpå.
        </p>
      </div>
    </div>
  );
}

export default SummaryStep;
